import { FormEvent, JSX, useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";

import Select from "src/components/filters/Select";
import PasswordInput from "src/components/common/PasswordInput";
import {
  ADMIN_ROLE_ID,
  ASSIGNABLE_ADMIN_ROLES,
  MANAGER_ROLE_ID,
} from "src/types/userManagement";
import type { AdminProgram, AdminUser } from "src/types/userManagement";

export type UserFormPayload = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  roleId: number;
  programIds: number[];
};

type UserFormModalProps = {
  show: boolean;
  mode: "create" | "edit";
  programs: AdminProgram[];
  isProgramsLoading?: boolean;
  editingUser?: AdminUser | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: UserFormPayload) => void;
};

const ROLE_OPTIONS = ASSIGNABLE_ADMIN_ROLES.map((role) => ({
  label: role.label,
  value: role.id,
}));

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const buildInitialForm = (editingUser?: AdminUser | null) => ({
  firstName: editingUser?.first_name ?? "",
  lastName: editingUser?.last_name ?? "",
  email: editingUser?.email ?? "",
  password: "",
  roleId: (editingUser?.role_id ?? "") as number | "",
  programIds: editingUser?.product_ids ?? ([] as number[]),
});

type FormState = ReturnType<typeof buildInitialForm>;
type FormErrors = Partial<Record<keyof FormState, string>>;

export default function UserFormModal({
  show,
  mode,
  programs,
  isProgramsLoading = false,
  editingUser = null,
  isSubmitting = false,
  onClose,
  onSubmit,
}: UserFormModalProps): JSX.Element {
  const [form, setForm] = useState<FormState>(() =>
    buildInitialForm(editingUser),
  );
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (show) {
      setForm(buildInitialForm(editingUser));
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editingUser]);

  const updateField = <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field !== "programIds") {
      setErrors((prev) =>
        prev[field as keyof FormErrors]
          ? { ...prev, [field]: undefined }
          : prev,
      );
    }
    // Switching role invalidates any stale "select a product" error from
    // the previous role (e.g. Manager -> Admin hides the checkbox list).
    if (field === "roleId") {
      setErrors((prev) =>
        prev.programIds ? { ...prev, programIds: undefined } : prev,
      );
    }
  };

  const toggleProgram = (programId: number) => {
    setForm((prev) => ({
      ...prev,
      programIds: prev.programIds.includes(programId)
        ? prev.programIds.filter((id) => id !== programId)
        : [...prev.programIds, programId],
    }));
    setErrors((prev) =>
      prev.programIds ? { ...prev, programIds: undefined } : prev,
    );
  };

  const isAdminRole = Number(form.roleId) === ADMIN_ROLE_ID;
  const isManagerRole = Number(form.roleId) === MANAGER_ROLE_ID;
  const isElusaUser = editingUser?.user_type === "elusa";

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: FormErrors = {};
    if (!form.firstName.trim())
      nextErrors.firstName = "First name is required.";
    if (!form.lastName.trim()) nextErrors.lastName = "Last name is required.";
    if (!form.email.trim()) nextErrors.email = "Email is required.";
    else if (!EMAIL_PATTERN.test(form.email.trim()))
      nextErrors.email = "Enter a valid email address.";
    if (mode === "create" && !form.password.trim())
      nextErrors.password = "Password is required.";
    if (!form.roleId) nextErrors.roleId = "Role is required.";
    if (isManagerRole && programs.length > 0 && form.programIds.length === 0)
      nextErrors.programIds = "Select at least one product for a Manager.";

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    // Admin gets every product in the program automatically — the checkbox
    // list is hidden for this role, so always send the full program set.
    const finalProgramIds = isAdminRole
      ? programs.map((program) => program.id)
      : form.programIds;

    onSubmit({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      password: form.password,
      roleId: Number(form.roleId),
      programIds: finalProgramIds,
    });
  };

  return (
    <Modal show={show} centered onHide={onClose}>
      <Form onSubmit={handleSubmit} className="create-user-form">
        <Modal.Header closeButton>
          <Modal.Title>
            {mode === "edit" ? "Edit User" : "Create User"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          <Form.Group controlId="user-first-name">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              value={form.firstName}
              onChange={(e) => updateField("firstName", e.target.value)}
              isInvalid={!!errors.firstName}
              placeholder="Enter first name"
              disabled={isSubmitting}
              autoFocus
            />
            <Form.Control.Feedback type="invalid">
              {errors.firstName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="user-last-name">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              value={form.lastName}
              onChange={(e) => updateField("lastName", e.target.value)}
              isInvalid={!!errors.lastName}
              placeholder="Enter last name"
              disabled={isSubmitting}
            />
            <Form.Control.Feedback type="invalid">
              {errors.lastName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="user-email">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
              isInvalid={!!errors.email}
              placeholder="Enter email address"
              disabled={isSubmitting}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          {mode === "create" && (
            <Form.Group controlId="user-password">
              <Form.Label>Password</Form.Label>
              <PasswordInput
                value={form.password}
                onChange={(value) => updateField("password", value)}
                error={errors.password}
                placeholder="Enter a temporary password"
                disabled={isSubmitting}
              />
            </Form.Group>
          )}

          <Form.Group controlId="user-role">
            <Form.Label className="d-block">Role</Form.Label>
            <Select
              id="roleId"
              name="roleId"
              placeholder="Select role"
              static_options={ROLE_OPTIONS}
              formValues={{ roleId: form.roleId }}
              onChange={(value) => updateField("roleId", Number(value))}
              disabled={isSubmitting}
            />
            {errors.roleId && (
              <div className="text-danger small mt-1">{errors.roleId}</div>
            )}
          </Form.Group>

          <Form.Group controlId="user-programs">
            <Form.Label className="d-block">Products</Form.Label>
            {isElusaUser ? (
              <div className="user-form__program-note">
                Elusa users have access to all products.
              </div>
            ) : isAdminRole ? (
              <div className="user-form__program-note">
                Admin is automatically assigned to all products in the program.
              </div>
            ) : isProgramsLoading ? (
              <div className="text-muted small">Loading programs…</div>
            ) : programs.length === 0 ? (
              <div className="text-muted small">No programs available.</div>
            ) : (
              <div className="user-form__program-list">
                {programs.map((program) => (
                  <Form.Check
                    key={program.id}
                    type="checkbox"
                    id={`user-program-${program.id}`}
                    label={program.name}
                    checked={form.programIds.includes(program.id)}
                    onChange={() => toggleProgram(program.id)}
                    disabled={isSubmitting}
                  />
                ))}
              </div>
            )}
            {errors.programIds && (
              <div className="text-danger small mt-1">{errors.programIds}</div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                {mode === "edit" ? "Saving..." : "Creating..."}
              </>
            ) : mode === "edit" ? (
              "Save Changes"
            ) : (
              "Create User"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
